import { debounce } from './theme.js';

/**
 * Predictive search dropdown.
 *
 * Fetches `/search/suggest` and swaps in the `predictive-results` section. Matches the live
 * theme's behaviour: 300ms debounce, one in-flight request at a time, arrow-key navigation
 * over the results, and a polite status announcement.
 */
export class PredictiveSearch extends HTMLElement {
  /* Live over-fetches so that Liquid-side filtering of hidden products does not leave a short
     row. 10 is the maximum the predictive search API accepts. */
  static FETCH_LIMIT = 10;
  static DEBOUNCE_MS = 300;

  constructor() {
    super();
    this.isOpen = false;
    this.abortController = undefined;
    this.previousTerm = '';
    this.activeIndex = -1;
  }

  /* Wiring lives here rather than in the constructor: connectedCallback re-runs if the element
     is moved in the DOM, and handlers must be stored so they can actually be removed later. */
  connectedCallback() {
    if (this.wired) return;

    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.statusRegion = this.querySelector('[data-predictive-search-status]');

    if (!this.input || !this.results) return;

    this.searchTerm = this.input.value.trim();
    this.previousTerm = this.searchTerm;

    this.onInput = debounce(() => this.onChange(), PredictiveSearch.DEBOUNCE_MS);
    this.onFocus = this.onFocus.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', this.onFocus);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.handleClickOutside);

    this.wired = true;
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('focus', this.onFocus);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.handleClickOutside);
    this.wired = false;
  }

  onChange() {
    const term = this.input.value.trim();

    if (!term.length) {
      this.abortController?.abort();
      this.clearResults();
      this.close();
      this.announce('');
      this.previousTerm = '';
      return;
    }

    /* If the shopper did not simply keep typing (select-all and retype, or a paste), the
       results on screen belong to an unrelated query — drop them so they cannot flash. */
    if (!term.startsWith(this.previousTerm)) this.clearResults();
    this.previousTerm = term;
    this.searchTerm = term;

    this.toggleLoading(true);
    this.announce(this.getAttribute('data-loading-text'));
    this.getSearchResults(term);
  }

  /* Focus alone must not refetch: the old handler called onChange() undebounced on every
     focus, costing a duplicate request each time the field was refocused. */
  onFocus() {
    const term = this.input.value.trim();
    if (!term.length) return;

    if (term === this.searchTerm && this.options.length) {
      this.open();
      return;
    }

    this.onChange();
  }

  async getSearchResults(searchTerm) {
    /* One controller per request. Previously a single controller was created once and never
       aborted, so a slow response for "sho" could land after "shoe" and overwrite it. */
    this.abortController?.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product',
      'resources[limit]': PredictiveSearch.FETCH_LIMIT,
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-results',
    });

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest?${params}`, {
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const markup = doc.querySelector('#shopify-section-predictive-results')?.innerHTML || '';

      this.updateResults(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.toggleLoading(false);
      this.close();
    }
  }

  updateResults(markup) {
    this.results.innerHTML = markup;
    this.identifyOptions();
    this.resetActiveOption();
    this.announce(
      this.querySelector('.predictive-search__count')?.textContent.trim() ||
        this.querySelector('[data-predictive-search-count]')?.textContent.trim()
    );

    /* `this.options` filters on offsetParent, so it is empty while the panel is still hidden
       from a previous empty query. Count the nodes in the new markup instead. */
    if (this.results.querySelectorAll('[data-predictive-search-option]').length) {
      this.open();
    } else {
      this.toggleLoading(false);
      this.results.hidden = true;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  /* aria-activedescendant needs a document-unique id. The dropdown section is fetched by both
     the header and the search-page instances, so ids are stamped per instance here instead of
     in Liquid, where the two panels would end up with the same ones. */
  identifyOptions() {
    const prefix = this.input.id || 'predictive-search';
    const options = [...this.results.querySelectorAll('[data-predictive-search-option]')];

    for (const [index, option] of options.entries()) {
      option.id = `${prefix}-option-${index}`;
    }
  }

  clearResults() {
    this.results.innerHTML = '';
    this.resetActiveOption();
  }

  /* `is-open` / `is-loading` on the host drive the panel's visibility in CSS. The panel wrapper
     carries a border and background, so without this it would render as an empty bordered box
     whenever the search bar was open with no query typed. */
  open() {
    this.toggleLoading(false);
    this.results.hidden = false;
    this.isOpen = true;
    this.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.results.hidden = true;
    this.isOpen = false;
    this.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resetActiveOption();
  }

  clearSearch(event) {
    event?.preventDefault();
    this.input.value = '';
    this.abortController?.abort();
    this.clearResults();
    this.close();
    this.previousTerm = '';
    this.searchTerm = '';
    this.input.focus();
  }

  /* --- keyboard navigation ------------------------------------------------------------- */

  get options() {
    /* offsetParent filters out anything hidden, matching how live walks its option list. */
    return Array.from(this.results.querySelectorAll('[data-predictive-search-option]')).filter(
      (option) => option.offsetParent !== null
    );
  }

  onKeydown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        this.input.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.moveActiveOption(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActiveOption(-1);
        break;
      case 'Home':
        if (!this.isOpen) return;
        event.preventDefault();
        this.setActiveOption(0);
        break;
      case 'End':
        if (!this.isOpen) return;
        event.preventDefault();
        this.setActiveOption(this.options.length - 1);
        break;
      case 'Enter': {
        /* With nothing highlighted, let the form submit through to /search as normal. */
        const active = this.options[this.activeIndex];
        if (!active) return;
        event.preventDefault();
        (active.querySelector('a') || active).click();
        break;
      }
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  }

  moveActiveOption(delta) {
    const total = this.options.length;
    if (!this.isOpen || !total) return;

    /* Wrap at both ends, as live does. */
    const next = (this.activeIndex + delta + total) % total;
    this.setActiveOption(next);
  }

  setActiveOption(index) {
    const options = this.options;
    if (!options.length) return;

    for (const option of options) {
      option.setAttribute('aria-selected', 'false');
    }

    const active = options[index];
    if (!active) return;

    active.setAttribute('aria-selected', 'true');
    this.activeIndex = index;

    /* The results panel scrolls, so a highlight below the fold would otherwise be invisible. */
    active.scrollIntoView({ block: 'nearest' });

    if (active.id) this.input.setAttribute('aria-activedescendant', active.id);
  }

  /* innerHTML replacement destroys the highlighted node, so a stale aria-activedescendant
     would point at an id that no longer exists and silence some screen readers. */
  resetActiveOption() {
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
  }

  /* --- helpers ------------------------------------------------------------------------- */

  handleClickOutside(event) {
    if (this.isOpen && !this.contains(event.target)) this.close();
  }

  toggleLoading(show) {
    this.querySelector('.predictive-search__loading')?.classList.toggle('hidden', !show);
    this.classList.toggle('is-loading', show);
  }

  /* The count string is rendered by Liquid so it stays localized and pluralized; this only
     copies it into the live region. */
  announce(message) {
    if (this.statusRegion) this.statusRegion.textContent = message || '';
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
